import { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Trophy, BarChart3 } from "lucide-react";
import { WidgetHead, shortRp } from "@/components/dashboard/ChartWidgets";
import DueCalendar from "@/components/dashboard/DueCalendar";
import { DebtCard, WeeklyBars, InsightHero, NeedsYou, ScheduleRows, TrendDots, SupplierHeat } from "@/components/dashboard/RefCards";
import { SupplierName } from "@/components/SupplierName";

function Ranking({ data }) {
  const max = data.ranking[0]?.value || 1;
  return (
    <Card className="p-6 h-full dash-rise" data-testid="dash-ranking">
      <WidgetHead icon={Trophy} title="Ranking Supplier" sub="Berdasarkan nilai pembelian" />
      <div className="space-y-3">
        {data.ranking.slice(0, 6).map((r, i) => (
          <div key={r.name} className="text-sm" data-testid={`dash-ranking-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`h-6 w-6 shrink-0 rounded-full text-xs font-semibold flex items-center justify-center ${i === 0 ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 shadow-sm"}`}>{i + 1}</span>
              <span className="font-medium text-slate-800 truncate flex-1 min-w-0" title={r.name}><SupplierName name={r.name} /></span>
              <span className="font-semibold text-slate-900 whitespace-nowrap" title={formatRupiah(r.value)}>Rp {shortRp(r.value)}</span>
            </div>
            <div className="flex items-center gap-2 pl-8">
              <div className="h-2 flex-1 rounded-full bg-zinc-200/70 overflow-hidden"><div className="h-full rounded-full hatch text-blue-600 bg-[#FAFAFA]" style={{ width: `${(r.value / max) * 100}%` }} /></div>
              <span className="text-[11px] text-slate-400 whitespace-nowrap">{formatNumber(r.rolls)} roll</span>
            </div>
          </div>
        ))}
        {data.ranking.length === 0 && <div className="text-sm text-slate-400">Belum ada data.</div>}
      </div>
    </Card>
  );
}

function PeriodStats({ data, period, setPeriod }) {
  const pv = data.paid_vs_unpaid;
  const tot = pv.paid_value + pv.unpaid_value;
  const stats = [
    ["Roll (periode)", formatNumber(data.purchases[period].rolls)],
    ["Nilai Pembelian", formatRupiah(data.purchases[period].value)],
    ["Yard / Meter", `${formatNumber(data.length.yard)} / ${formatNumber(data.length.meter)}`],
    ["Jatuh Tempo Bulan Ini", formatRupiah(data.due_month.value), `${formatNumber(data.due_month.rolls)} roll`],
    ["Lunas", formatRupiah(pv.paid_value), `${formatNumber(pv.paid_rolls)} roll`],
    ["Belum Lunas", formatRupiah(pv.unpaid_value), `${formatNumber(pv.unpaid_rolls)} roll`],
    ["Dibayar via Transfer", formatRupiah(data.net_payment.transfer)],
    ["Dibayar via Giro", formatRupiah(data.net_payment.giro)],
  ];
  return (
    <Card className="p-6 flex-1 dash-rise" data-testid="dash-period-stats">
      <WidgetHead icon={BarChart3} title="Pembelian & Pelunasan" sub="Ringkasan periode terpilih">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 rounded-full" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Harian</SelectItem>
            <SelectItem value="week">Mingguan</SelectItem>
            <SelectItem value="month">Bulanan</SelectItem>
            <SelectItem value="year">Tahunan</SelectItem>
          </SelectContent>
        </Select>
      </WidgetHead>
      <div className="grid grid-cols-2 gap-2">
        {stats.map(([l, v, s]) => (
          <div key={l} className="rounded-2xl bg-white p-3 min-w-0">
            <div className="text-xs text-zinc-400 leading-tight">{l}</div>
            <div className="text-base font-semibold text-zinc-900 mt-1 break-words">{v}</div>
            {s && <div className="text-xs text-zinc-400">{s}</div>}
          </div>
        ))}
      </div>
      <div className="mt-5">
        <div className="flex h-2.5 rounded-full overflow-hidden bg-zinc-200/70">
          <div className="bg-blue-600" style={{ width: `${tot ? (pv.paid_value / tot) * 100 : 0}%` }} />
          <div className="hatch text-rose-400 flex-1" />
        </div>
        <div className="flex justify-between text-[11px] text-zinc-400 mt-1"><span>Lunas</span><span>Belum lunas</span></div>
      </div>
    </Card>
  );
}

function SettledToday({ rem }) {
  if (!rem?.settled_today?.length) return null;
  return (
    <Card className="p-6 h-auto border-emerald-200 dash-rise" data-testid="dash-settled-today">
      <div className="text-base font-bold flex items-center gap-2 text-emerald-700 mb-3"><CheckCircle2 className="h-4 w-4" />Lunas Hari Ini</div>
      <div className="space-y-1 text-sm">
        {rem.settled_today.map((s) => (
          <div key={s.schedule.code} className="flex justify-between gap-3"><span className="text-slate-600 truncate"><SupplierName name={s.supplier_name} /></span><span className="font-semibold whitespace-nowrap">{formatRupiah(s.schedule.money_out)}</span></div>
        ))}
      </div>
    </Card>
  );
}

function SettledOrStats({ rem, ...props }) {
  return (
    <div className="flex flex-col gap-4 h-full">
      <SettledToday rem={rem} />
      <PeriodStats {...props} />
    </div>
  );
}

function DashboardInner() {
  const { isOwner } = useAuth();
  const [period, setPeriod] = useState("month");
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [rem, setRem] = useState(null);

  const load = useCallback(() => {
    api.get(`/dashboard?period=${period}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/reminders").then((r) => setRem(r.data)).catch(() => {});
  }, [period]);
  const loadCharts = useCallback(() => {
    api.get(`/dashboard/charts?months=${months}`).then((r) => setCharts(r.data)).catch((e) => toast.error(apiError(e)));
  }, [months]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCharts(); }, [loadCharts]);

  if (!data || !charts) return <div className="text-slate-500" data-testid="dashboard-loading">Memuat...</div>;

  return (
    <div className="space-y-4" data-testid="bento-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 dash-stagger">
        <div className="md:col-span-6 lg:col-span-4"><DebtCard data={data} charts={charts} /></div>
        <div className="md:col-span-6 lg:col-span-4"><WeeklyBars weekly={charts.upcoming_weekly} /></div>
        <div className="md:col-span-12 lg:col-span-4"><InsightHero data={data} charts={charts} /></div>

        <div className="md:col-span-12 lg:col-span-8"><ScheduleRows items={charts.upcoming_items} today={charts.effective_date} /></div>
        <div className="md:col-span-12 lg:col-span-4"><NeedsYou data={data} charts={charts} rem={rem} isOwner={isOwner} /></div>

        <div className="md:col-span-12 lg:col-span-5"><TrendDots trend={charts.trend} months={months} onMonths={setMonths} /></div>
        <div className="md:col-span-12 lg:col-span-7"><SupplierHeat debt={charts.debt_by_supplier} ranking={data.ranking} /></div>

        <div className="md:col-span-6 lg:col-span-4"><DueCalendar key={charts.effective_date} items={charts.upcoming_items} paidItems={charts.paid_items || []} today={charts.effective_date} /></div>
        <div className="md:col-span-6 lg:col-span-4"><Ranking data={data} /></div>
        <div className="md:col-span-12 lg:col-span-4"><SettledOrStats data={data} period={period} setPeriod={setPeriod} rem={rem} /></div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  return (
    <PageShell title="Dashboard">
      <DashboardInner />
    </PageShell>
  );
}
